import { ChangeDetectorRef, Component, forwardRef, Injector } from "@angular/core";
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, FormBuilder, FormGroup, FormArray } from "@angular/forms";
import { AbstractWorkflowFormComponent } from "../../../@core/abstract-workflow-form-component";
import { MultipackCommercialPlanningModel } from "../commercial-planning.model";
import { WorkflowComponentsService } from "../../../workflow-components.service";
import { MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS, MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS_LABELS, MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS_OPTIONS, MULTIPACK_COMMERCIAL_PLANNING_PRIORITES_OPTIONS, MultipackCommercialPlanningService, StartupInfos_MultipackCommercialPlanning } from "../commercial-planning.service";
import { v4 as uuidv4 } from 'uuid';
import { Model_DeclencheurActionDatasourceFromMultipackInputAgendaPlannerOptions, TYPE_DECLENCHEUR } from "src/app/app.models";
import * as moment from 'moment';
import { AuthService } from "src/app/core-api/services/auth/auth.service";

@Component({
    selector: 'abw-multipack-commercial-planning-form',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultipackCommercialPlanningFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => MultipackCommercialPlanningFormComponent),
            multi: true
        }
    ],
    templateUrl: './commercial-planning-form.component.html',
    styleUrls: ['./commercial-planning-form.component.scss']
})
export class MultipackCommercialPlanningFormComponent extends AbstractWorkflowFormComponent<MultipackCommercialPlanningModel, string> {
    loading = false;
    startupInfos: StartupInfos_MultipackCommercialPlanning;
    filteredTiers: { [key: string]: any[] } = {};
    errMessage: string;
    planningFormGroup = this.fb.group({
        PLANNING_DATE: [moment().format("YYYY-MM-DD")],
        PLANNING_ITEMS: this.fb.array([])
    });
    operations: any[] = MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS_OPTIONS;
    priorites: any[] = MULTIPACK_COMMERCIAL_PLANNING_PRIORITES_OPTIONS;

    constructor(
        private multipackCommercialPlanningService: MultipackCommercialPlanningService,
        _workflowComponentsService: WorkflowComponentsService,
        _cdf: ChangeDetectorRef,
        _injector: Injector,
        private fb: FormBuilder,
        private authService: AuthService
    ) {
        super(_workflowComponentsService, _cdf, _injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.planningFormGroup.valueChanges.subscribe(planningValue => {
            this.onValueChanged(planningValue);
        });
        this.doGetStartupInfos().then(() => this.doGetPlanningDatas());
    }

    get planningFormDate() {
        return this.planningFormGroup.controls.PLANNING_DATE;
    }

    get planningFormArray() {
        return this.planningFormGroup.controls.PLANNING_ITEMS as FormArray;
    }

    addRow(rowData?: any) {
        const newRow = this.fb.group({
            ID: [rowData?.ID || uuidv4()],
            AGENT_ID: [rowData?.AGENT_ID || null],
            AGENT_FULLNAME: [rowData?.AGENT_FULLNAME || null],
            OPERATION_ID: [rowData?.OPERATION_ID || 'RECOUVREMENT'],
            OPERATION_NAME: [rowData?.OPERATION_NAME || 'Recouvrement'],
            TIERS_ID: [rowData?.TIERS_ID || null],
            TIERS_TYPE: [rowData?.TIERS_TYPE || null],
            TIERS_FULLNAME: [rowData?.TIERS_FULLNAME || null],
            SOLDE: [rowData?.SOLDE || 0],
            MONTANT: [rowData?.MONTANT || 0],
            PRIORITE: [rowData?.PRIORITE || 'NORMAL'],
            COMMENTAIRE: [rowData?.COMMENTAIRE || null],
        });
        this.planningFormArray.push(newRow);
        this.planningFormGroup.updateValueAndValidity();
        this._cdf.detectChanges();
        this.filterTiers(this.planningFormArray.length - 1, '');
    }

    removeRow(index: number) {
        if (index >= 0 && index < this.planningFormArray.length) {
            const rowId = (this.planningFormArray.at(index) as FormGroup).controls['ID'].value;
            this.planningFormArray.removeAt(index);
            this.planningFormGroup.updateValueAndValidity();
            this.planningFormArray.markAsTouched();
            delete this.filteredTiers[rowId];
        }
    }

    clearPlanning() {
        this.planningFormArray.clear();
        this.planningFormGroup.updateValueAndValidity();
        this.planningFormArray.markAsUntouched();
        this._cdf.detectChanges();
        this.filteredTiers = {};
    }

    async doGetStartupInfos() {
        this.loading = true;
        try {
            this.startupInfos = await this.multipackCommercialPlanningService.doGetStartupInfos({
                NUM_WORKFLOW_COLUMNS_MAPPING: this.model.additional.NUM_WORKFLOW_COLUMNS_MAPPING,

                NUM_DS_AGENTS: this.model.additional.NUM_DATASOURCE_AGENTS.NUM_DATASOURCE,
                ID_DS_AGENT_COLUMN: this.model.additional.ID_DATASOURCE_AGENTS_COLUMN,
                FULLNAME_DS_AGENT_COLUMN: this.model.additional.FULLNAME_DATASOURCE_AGENTS_COLUMN,

                NUM_DS_TIERS: this.model.additional.NUM_DATASOURCE_TIERS.NUM_DATASOURCE,
                ID_DS_TIERS_COLUMN: this.model.additional.ID_DATASOURCE_TIERS_COLUMN,
                FULLNAME_DS_TIERS_COLUMN: this.model.additional.FULLNAME_DATASOURCE_TIERS_COLUMN,
                TYPE_DS_TIERS_COLUMN: this.model.additional.TYPE_DATASOURCE_TIERS_COLUMN,
                SOLDE_DS_TIERS_COLUMN: this.model.additional.SOLDE_DATASOURCE_TIERS_COLUMN
            });
            //this.filteredTiers = this.startupInfos.TIERS;
            this.loading = false;
        } catch (error) {
            this.errMessage = error;
            this.loading = false;
        }
        this._cdf.detectChanges();
    }

    async doGetPlanningDatas() {
        const datePlanningValue = this.planningFormDate.value;
        const datePlanningColumn = this.startupInfos.COLUMNS_MAPPING.find(mapping => mapping.DEFAULT_VALUE == "DATE_PLANNING")?.DATASOURCE_COLUMN;
        const agentIdColumn = this.startupInfos.COLUMNS_MAPPING.find(mapping => mapping.DEFAULT_VALUE == "AGENT_ID")?.DATASOURCE_COLUMN;
        this.loading = true;
        try {
            this.clearPlanning();
            const planningDatas = await this.multipackCommercialPlanningService.doGetPlanningDatasAction({
                NUM_DS_PLANNING: this.startupInfos.NUM_DS_PLANNING,
                DATE_PLANNING_COLUMN: datePlanningColumn,
                DATE_PLANNING_VALUE: datePlanningValue,
                AGENT_ID_COLUMN: agentIdColumn,
                AGENT_ID_VALUE: this.model.showOnlyAuthenticatedUser ? this.authService.AuthSession.User.USERNAME : null,
            });
            if (!!this.startupInfos.COLUMNS_MAPPING && !!planningDatas && planningDatas.length > 0) {
                planningDatas.forEach(planningData => {
                    const planningItem: any = {};
                    this.startupInfos.COLUMNS_MAPPING.forEach(mapping => {
                        if (mapping.TYPE_DEFAULT_VALUE == 4) {
                            planningItem[mapping.DEFAULT_VALUE] = planningData[mapping.DATASOURCE_COLUMN];
                        }
                    });
                    this.addRow(planningItem);
                });
            }
        } catch (error) {
            this.errMessage = error;
        } finally {
            this.loading = false;
            this._cdf.detectChanges();
        }
    }

    onAgentChanged(agentId: any, index: number) {
        const agent = this.startupInfos.AGENTS.find(t => t.AGENT_ID === agentId);
        if (!agent) {
            console.warn('Agent not found for ID:', agentId);
            return;
        }
        (this.planningFormArray.at(index) as FormGroup).controls['AGENT_FULLNAME'].setValue(agent.AGENT_FULLNAME);
    }

    onOperationChanged(operationId: MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS, index: number) {
        (this.planningFormArray.at(index) as FormGroup).controls['OPERATION_NAME'].setValue(MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS_LABELS[operationId]);
        this.filterTiers(index, '');
        (this.planningFormArray.at(index) as FormGroup).controls['TIERS_ID'].setValue(null);
    }

    onTiersChanged(tiersId: any, index: number) {
        const tiers = this.startupInfos.TIERS.find(t => t.TIERS_ID === tiersId);
        if (!tiers) {
            console.warn('Tiers not found for ID:', tiersId);
            return;
        }
        (this.planningFormArray.at(index) as FormGroup).controls['TIERS_TYPE'].setValue(tiers.TIERS_TYPE);
        (this.planningFormArray.at(index) as FormGroup).controls['TIERS_FULLNAME'].setValue(tiers.TIERS_FULLNAME);
        (this.planningFormArray.at(index) as FormGroup).controls['SOLDE'].setValue(tiers.TIERS_SOLDE);
    }

    onDateChanged(dateChangedEvent: any, dateDisplayInputEl: any) {
        const formatedDate = moment(dateChangedEvent.value).format("YYYY-MM-DD");
        if (formatedDate != this.planningFormDate.value) {
            let confirmation = this.planningFormArray.touched && !this.model.readOnly ? confirm("La date de planification a été modifiée. Voulez-vous recharger les données de planification pour cette date ?") : true;
            if (!confirmation) {
                dateDisplayInputEl.value = moment(this.planningFormDate.value).format("DD/MM/YYYY");
                dateChangedEvent.preventDefault();
                return;
            }

            this.planningFormDate.patchValue(formatedDate);
            this.doGetPlanningDatas();
        }
    }

    handleFilter(value: string, index: number) {
        this.filterTiers(index, value);
    }

    filterTiers(index: number, filterValue: string) {
        let filteredTiers = this.startupInfos.TIERS;

        const rowId = (this.planningFormArray.at(index) as FormGroup).controls['ID'].value;
        const operationId = (this.planningFormArray.at(index) as FormGroup).controls['OPERATION_ID'].value;
        let ctType: number = null;
        if (operationId === MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS.RECOUVREMENT) {
            ctType = 0; // Client
        } else if (operationId === MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS.FOURNISSEUR) {
            ctType = 1; // Fournisseur
        } else if (operationId === MULTIPACK_COMMERCIAL_PLANNING_OPERATIONS.BANQUE) {
            ctType = 5; // Autre
        }
        filteredTiers = filteredTiers.filter(t => t.TIERS_TYPE === ctType);

        if (filterValue && filterValue.trim() !== '') {
            filteredTiers = filteredTiers.filter(t => !!t && !!t['TIERS_FULLNAME'] && t['TIERS_FULLNAME'].toLowerCase().indexOf(filterValue.toLowerCase()) !== -1);
        }
        this.filteredTiers[rowId] = filteredTiers;
    }

}